/**
 * Lightweight mmk_book actor (hand-rolled IDL — no bindgen required for SPA).
 */
import { Actor, type ActorSubclass, type HttpAgent } from "@icp-sdk/core/agent";
import type { IDL } from "@icp-sdk/core/candid";
import type { Principal } from "@icp-sdk/core/principal";

export const mmkBookIdlFactory: IDL.InterfaceFactory = ({ IDL: I }) => {
	const S = I.Variant({ BuyMmk: I.Null, SellMmk: I.Null });
	const RO = I.Variant({ ok: I.Nat, err: I.Text });
	const RV = I.Variant({ ok: I.Null, err: I.Text });
	const Ord = I.Record({
		id: I.Nat,
		owner: I.Principal,
		side: S,
		priceE8: I.Nat,
		sizeMmkE8: I.Nat,
		filledMmkE8: I.Nat,
		escrowUsdmE8: I.Nat,
		escrowMmkE8: I.Nat,
		createdNs: I.Int,
		status: I.Variant({
			Open: I.Null,
			Filled: I.Null,
			Cancelled: I.Null,
		}),
	});
	return I.Service({
		health: I.Func(
			[],
			[
				I.Record({
					status: I.Text,
					openOrders: I.Nat,
					settlementMode: I.Bool,
				}),
			],
			["query"],
		),
		getStatus: I.Func(
			[],
			[
				I.Record({
					enabled: I.Bool,
					activateBps: I.Nat,
					makerFeeBps: I.Nat,
					takerFeeBps: I.Nat,
					openOrders: I.Nat,
					tradeCount: I.Nat,
					lastPriceE8: I.Opt(I.Nat),
					midE8: I.Opt(I.Nat),
					protocol: I.Opt(I.Principal),
					settlementMode: I.Bool,
					usdmLedger: I.Opt(I.Principal),
					mmkLedger: I.Opt(I.Principal),
					pendingFeeUsdmE8: I.Nat,
				}),
			],
			["query"],
		),
		getOrderBook: I.Func(
			[I.Nat],
			[
				I.Record({
					bids: I.Vec(I.Tuple(I.Nat, I.Nat)),
					asks: I.Vec(I.Tuple(I.Nat, I.Nat)),
					lastPriceE8: I.Opt(I.Nat),
					midE8: I.Opt(I.Nat),
				}),
			],
			["query"],
		),
		getMyOrders: I.Func([], [I.Vec(Ord)], ["query"]),
		getTrades: I.Func(
			[I.Nat, I.Nat],
			[
				I.Record({
					total: I.Nat,
					trades: I.Vec(
						I.Record({
							id: I.Nat,
							buyOrderId: I.Nat,
							sellOrderId: I.Nat,
							priceE8: I.Nat,
							sizeMmkE8: I.Nat,
							buyer: I.Principal,
							seller: I.Principal,
							makerFeeUsdmE8: I.Nat,
							takerFeeUsdmE8: I.Nat,
							tsNs: I.Int,
						}),
					),
				}),
			],
			["query"],
		),
		isOrderBookActive: I.Func([], [I.Bool], []),
		placeLimit: I.Func([S, I.Nat, I.Nat], [RO], []),
		cancelOrder: I.Func([I.Nat], [RV], []),
	});
};

export type MmkBookSide = { BuyMmk: null } | { SellMmk: null };

export type MmkBookService = {
	health: () => Promise<{
		status: string;
		openOrders: bigint;
		settlementMode: boolean;
	}>;
	getStatus: () => Promise<{
		enabled: boolean;
		activateBps: bigint;
		makerFeeBps: bigint;
		takerFeeBps: bigint;
		openOrders: bigint;
		tradeCount: bigint;
		lastPriceE8: [] | [bigint];
		midE8: [] | [bigint];
		protocol: [] | [Principal];
		settlementMode: boolean;
		usdmLedger: [] | [Principal];
		mmkLedger: [] | [Principal];
		pendingFeeUsdmE8: bigint;
	}>;
	getOrderBook: (depth: bigint) => Promise<{
		bids: [bigint, bigint][];
		asks: [bigint, bigint][];
		lastPriceE8: [] | [bigint];
		midE8: [] | [bigint];
	}>;
	getMyOrders: () => Promise<
		Array<{
			id: bigint;
			side: MmkBookSide;
			priceE8: bigint;
			sizeMmkE8: bigint;
			filledMmkE8: bigint;
			status: { Open: null } | { Filled: null } | { Cancelled: null };
		}>
	>;
	isOrderBookActive: () => Promise<boolean>;
	placeLimit: (
		side: MmkBookSide,
		priceE8: bigint,
		sizeMmkE8: bigint,
	) => Promise<{ ok: bigint } | { err: string }>;
	cancelOrder: (id: bigint) => Promise<{ ok: null } | { err: string }>;
	getTrades: (
		offset: bigint,
		limit: bigint,
	) => Promise<{
		total: bigint;
		trades: Array<{
			id: bigint;
			buyOrderId: bigint;
			sellOrderId: bigint;
			priceE8: bigint;
			sizeMmkE8: bigint;
			buyer: Principal;
			seller: Principal;
			makerFeeUsdmE8: bigint;
			takerFeeUsdmE8: bigint;
			tsNs: bigint;
		}>;
	}>;
};

export type MmkBookActor = ActorSubclass<MmkBookService>;

export function getMmkBookCanisterId(
	env: Record<string, string | undefined> = import.meta.env as Record<
		string,
		string | undefined
	>,
): string | null {
	const id =
		env.VITE_CANISTER_ID_MMK_BOOK?.trim() ||
		env.VITE_MMK_BOOK_CANISTER_ID?.trim() ||
		"";
	return id || null;
}

export function createMmkBookActor(
	canisterId: string,
	agent: HttpAgent,
): MmkBookActor {
	return Actor.createActor<MmkBookService>(mmkBookIdlFactory, {
		agent,
		canisterId,
	});
}
